import React, { useState, useEffect } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { 
  ArrowLeft, Calendar, Clock, MapPin, User, Wrench, FileText,
  CheckCircle, AlertCircle, Loader2, X, DollarSign
} from "lucide-react";
import { Badge } from "../../components/ui/badge";
import { Button } from "../../components/ui/button";
import { usePortalAuth } from "./PortalLayout";
import axios from "axios";

import { formatJobScheduleDisplay } from "../../utils/dateUtils";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

const getStatusColor = (status, fallbackHex) => {
  if (fallbackHex) return fallbackHex;
  const map = { completed: "#22C55E", in_progress: "#3B82F6", scheduled: "#8B5CF6", pending: "#F59E0B", deposit_collected: "#14B8A6", cancelled: "#EF4444", paid: "#22C55E", partially_paid: "#F59E0B", sent: "#3B82F6", overdue: "#EF4444" };
  return map[status] || "#94A3B8";
};

const formatDate = (dateStr) => {
  if (!dateStr) return "N/A";
  return new Date(dateStr).toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric" });
};

const formatDateFull = (dateStr) => {
  if (!dateStr) return "N/A";
  return new Date(dateStr).toLocaleDateString("en-US", { weekday: "long", year: "numeric", month: "long", day: "numeric" });
};

// ======================== JOB PREVIEW MODAL ========================

const JobPreviewModal = ({ job, onClose }) => {
  if (!job) return null;

  return (
    <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-end sm:items-center justify-center z-50 p-0 sm:p-4" onClick={onClose} data-testid="job-preview-modal">
      <div
        className="relative bg-white rounded-t-2xl sm:rounded-2xl w-full sm:max-w-lg max-h-[85vh] overflow-auto"
        style={{ boxShadow: "0 -4px 24px rgba(0,0,0,0.12)" }}
        onClick={e => e.stopPropagation()}
      >
        {/* Handle bar (mobile) */}
        <div className="sm:hidden flex justify-center pt-2 pb-1">
          <div className="w-10 h-1 rounded-full bg-slate-200" />
        </div>

        {/* Header */}
        <div className="sticky top-0 z-10 bg-white px-5 py-4 flex items-start justify-between gap-3 border-b border-slate-100">
          <div className="flex-1 min-w-0">
            <h2 className="text-lg font-semibold text-slate-800 font-['Outfit']">{job.title || job.job_type_name || "Service Job"}</h2>
            <p className="text-xs text-slate-300 mt-0.5">Job #{job.job_display_id || job.job_id?.slice(0, 8)}</p>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            <Badge className="text-[10px] px-2.5 py-1 border" style={{ background: `${getStatusColor(job.status, job.status_color)}08`, color: getStatusColor(job.status, job.status_color), borderColor: `${getStatusColor(job.status, job.status_color)}20` }}>
              {job.status_label || job.status?.replace("_", " ")}
            </Badge>
            <button onClick={onClose} className="text-slate-300 hover:text-slate-600 transition-colors p-1" data-testid="close-job-preview">
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        <div className="p-5 space-y-4">
          {/* Schedule & Location */}
          <div className="space-y-3">
            {job.scheduled_date && (
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-xl bg-blue-50 flex items-center justify-center flex-shrink-0">
                  <Calendar className="w-4 h-4 text-blue-500" />
                </div>
                <div>
                  <p className="text-[10px] text-slate-300 uppercase tracking-wider">Date</p>
                  <p className="text-sm font-medium text-slate-700">{formatDateFull(job.scheduled_date)}</p>
                </div>
              </div>
            )}
            {(formatJobScheduleDisplay(job)) && (
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-xl bg-purple-50 flex items-center justify-center flex-shrink-0">
                  <Clock className="w-4 h-4 text-purple-500" />
                </div>
                <div>
                  <p className="text-[10px] text-slate-300 uppercase tracking-wider">Time</p>
                  <p className="text-sm font-medium text-slate-700">{formatJobScheduleDisplay(job)}</p>
                </div>
              </div>
            )}
            {job.address && (
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-xl bg-emerald-50 flex items-center justify-center flex-shrink-0">
                  <MapPin className="w-4 h-4 text-emerald-500" />
                </div>
                <div>
                  <p className="text-[10px] text-slate-300 uppercase tracking-wider">Location</p>
                  <p className="text-sm font-medium text-slate-700">{job.address}</p>
                </div>
              </div>
            )}
            {job.technician_name && (
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-xl bg-amber-50 flex items-center justify-center flex-shrink-0">
                  <User className="w-4 h-4 text-amber-500" />
                </div>
                <div>
                  <p className="text-[10px] text-slate-300 uppercase tracking-wider">Technician</p>
                  <p className="text-sm font-medium text-slate-700">{job.technician_name}</p>
                </div>
              </div>
            )}
          </div>

          {/* Description */}
          {job.description && (
            <div className="bg-slate-50 rounded-xl p-4">
              <p className="text-[10px] text-slate-400 uppercase tracking-wider mb-1.5">Description</p>
              <p className="text-sm text-slate-600 leading-relaxed">{job.description}</p>
            </div>
          )}

          {/* Notes */}
          {job.notes && (
            <div className="bg-slate-50 rounded-xl p-4">
              <p className="text-[10px] text-slate-400 uppercase tracking-wider mb-1.5">Notes</p>
              <p className="text-sm text-slate-600 leading-relaxed">{job.notes}</p>
            </div>
          )}

          {/* Pricing */}
          {(job.price > 0 || job.total_charged > 0) && (
            <div className="bg-emerald-50 rounded-xl p-4">
              <p className="text-[10px] text-emerald-600 uppercase tracking-wider mb-1.5">Pricing</p>
              <p className="text-lg font-bold text-emerald-700">${(job.total_charged || job.price || 0).toFixed(2)}</p>
            </div>
          )}

          {/* Related Invoices */}
          {job.invoices && job.invoices.length > 0 && (
            <div>
              <p className="text-[10px] text-slate-400 uppercase tracking-wider mb-2">Invoices</p>
              <div className="space-y-2">
                {job.invoices.map((inv) => (
                  <Link key={inv.invoice_id} to={`/portal/invoices/${inv.invoice_id}`} onClick={onClose}
                    className="flex items-center justify-between p-3 rounded-xl bg-slate-50 hover:bg-slate-100 transition-colors" data-testid={`preview-invoice-${inv.invoice_id}`}>
                    <div className="flex items-center gap-2">
                      <FileText className="w-4 h-4 text-slate-400" />
                      <span className="text-sm font-medium text-slate-600">#{inv.invoice_number || inv.invoice_id?.slice(0, 8)}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-semibold text-slate-700">${(inv.total || 0).toFixed(2)}</span>
                      <Badge className="text-[10px] px-1.5 py-0 border" style={{ background: `${getStatusColor(inv.status)}08`, color: getStatusColor(inv.status), borderColor: `${getStatusColor(inv.status)}20` }}>
                        {inv.status}
                      </Badge>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="sticky bottom-0 bg-white border-t border-slate-100 p-4 flex gap-2">
          <Link to={`/portal/jobs/${job.job_id}`} onClick={onClose} className="flex-1">
            <Button className="w-full bg-[#0046BE] hover:bg-[#0036A0] text-white rounded-xl text-sm" data-testid="view-full-details-btn">
              View Full Details
            </Button>
          </Link>
          <Button variant="outline" onClick={onClose} className="rounded-xl border-slate-200 text-slate-500" data-testid="close-preview-btn">
            Close
          </Button>
        </div>
      </div>
    </div>
  );
};


// ======================== JOBS LIST ========================

const PortalJobs = () => {
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [previewJob, setPreviewJob] = useState(null);
  const [loadingPreview, setLoadingPreview] = useState(false);

  useEffect(() => {
    const fetchJobs = async () => {
      setLoading(true);
      const token = localStorage.getItem("portal_token");
      const config = { headers: { Authorization: `Bearer ${token}` } };
      try {
        const statusParam = filter !== "all" ? `&status=${filter}` : "";
        const res = await axios.get(`${API}/portal/jobs?limit=100${statusParam}`, config);
        setJobs(res.data.jobs);
      } catch (error) {
        console.error("Error fetching jobs:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchJobs();
  }, [filter]);

  const openPreview = async (job) => {
    // Try to fetch full details
    setLoadingPreview(true);
    const token = localStorage.getItem("portal_token");
    try {
      const res = await axios.get(`${API}/portal/jobs/${job.job_id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPreviewJob(res.data);
    } catch {
      setPreviewJob(job); // Fallback to list data
    } finally {
      setLoadingPreview(false);
    }
  };

  const filters = ["all", "pending", "scheduled", "in_progress", "deposit_collected", "completed", "completed_pending_payment", "cancelled"];

  if (loading) {
    return <div className="flex items-center justify-center h-64"><Loader2 className="w-6 h-6 animate-spin text-[#0046BE]" /></div>;
  }

  return (
    <div className="space-y-5 sm:space-y-6" data-testid="portal-jobs">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <h1 className="text-xl sm:text-2xl font-bold text-slate-800 font-['Outfit']">My Jobs</h1>
        <div className="flex gap-2 overflow-x-auto pb-1 scrollbar-none">
          {filters.map((status) => (
            <button
              key={status}
              onClick={() => setFilter(status)}
              data-testid={`portal-job-filter-${status}`}
              className={`px-3.5 py-1.5 rounded-lg text-xs font-medium whitespace-nowrap transition-all duration-200 ${
                filter === status
                  ? "bg-[#0046BE] text-white shadow-sm"
                  : "bg-white text-slate-400 hover:text-slate-600 border border-slate-100"
              }`}
            >
              {status === "all" ? "All" : status.replace("_", " ").replace(/\b\w/g, l => l.toUpperCase())}
            </button>
          ))}
        </div>
      </div>

      {jobs.length === 0 ? (
        <div className="bg-white rounded-2xl py-16 text-center" style={{boxShadow: '0 1px 3px rgba(0,0,0,0.04)', border: '1px solid rgba(0,0,0,0.04)'}}>
          <Wrench className="w-10 h-10 mx-auto text-slate-200 mb-3" />
          <p className="text-slate-300 text-sm">No jobs found</p>
        </div>
      ) : (
        <div className="space-y-2.5">
          {jobs.map((job) => (
            <button key={job.job_id} onClick={() => openPreview(job)} className="w-full text-left" data-testid={`portal-job-card-${job.job_id}`}>
              <div className="group bg-white rounded-2xl p-4 sm:p-5 transition-all duration-200 hover:translate-y-[-1px] active:scale-[0.99]" style={{boxShadow: '0 1px 3px rgba(0,0,0,0.04)', border: '1px solid rgba(0,0,0,0.04)'}}>
                <div className="flex items-start gap-3 sm:gap-4">
                  <div className="w-1 h-12 rounded-full flex-shrink-0 mt-0.5" style={{background: getStatusColor(job.status)}} />
                  <div className="flex-1 min-w-0">
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                      <div>
                        <h3 className="text-sm sm:text-[15px] font-semibold text-slate-700">{job.title || job.job_type_name || "Service Job"}</h3>
                        <p className="text-xs text-slate-300 mt-0.5">Job #{job.job_display_id || job.job_id?.slice(0, 8)}</p>
                      </div>
                      <div className="flex items-center gap-3">
                        <Badge className="text-[10px] px-2 py-0.5 border" style={{background: `${getStatusColor(job.status)}08`, color: getStatusColor(job.status), borderColor: `${getStatusColor(job.status)}20`}}>
                          {job.status?.replace("_", " ")}
                        </Badge>
                        <span className="text-xs text-slate-300 flex items-center gap-1">
                          <Calendar className="w-3 h-3" />
                          {formatDate(job.scheduled_date || job.created_at)}
                        </span>
                      </div>
                    </div>
                    {job.address && (
                      <p className="text-xs text-slate-300 flex items-center gap-1.5 mt-2">
                        <MapPin className="w-3 h-3 flex-shrink-0" />{job.address}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            </button>
          ))}
        </div>
      )}

      {/* Job Preview Modal */}
      {previewJob && <JobPreviewModal job={previewJob} onClose={() => setPreviewJob(null)} />}
    </div>
  );
};

const PortalJobDetails = () => {
  const { jobId } = useParams();
  const navigate = useNavigate();
  const [job, setJob] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchJob = async () => {
      const token = localStorage.getItem("portal_token");
      const config = { headers: { Authorization: `Bearer ${token}` } };
      try {
        const res = await axios.get(`${API}/portal/jobs/${jobId}`, config);
        setJob(res.data);
      } catch { /* ignore */ } finally { setLoading(false); }
    };
    fetchJob();
  }, [jobId]);

  if (loading) return <div className="flex items-center justify-center h-64"><Loader2 className="w-6 h-6 animate-spin text-[#0046BE]" /></div>;

  if (!job) return (
    <div className="text-center py-16">
      <AlertCircle className="w-10 h-10 mx-auto text-slate-200 mb-3" />
      <p className="text-slate-400 text-sm">Job not found</p>
      <Button variant="outline" size="sm" onClick={() => navigate("/portal/jobs")} className="mt-4 rounded-xl">Back to Jobs</Button>
    </div>
  );

  return (
    <div className="space-y-5" data-testid="portal-job-details">
      <Button variant="ghost" size="sm" onClick={() => navigate("/portal/jobs")} className="text-slate-400 hover:text-slate-600 -ml-2 rounded-xl" data-testid="job-back-btn">
        <ArrowLeft className="w-4 h-4 mr-1.5" /> Back
      </Button>

      {/* Header */}
      <div className="bg-white rounded-2xl p-5 sm:p-6" style={{boxShadow: '0 1px 3px rgba(0,0,0,0.04)', border: '1px solid rgba(0,0,0,0.04)'}}>
        <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-3">
          <div>
            <h1 className="text-xl sm:text-2xl font-bold text-slate-800 font-['Outfit']">{job.title || job.job_type_name || "Service Job"}</h1>
            <p className="text-slate-300 text-sm mt-0.5">Job #{job.job_id?.slice(0, 8)}</p>
          </div>
          <Badge className="text-xs px-3 py-1 border self-start" style={{background: `${getStatusColor(job.status)}08`, color: getStatusColor(job.status), borderColor: `${getStatusColor(job.status)}20`}}>
            {job.status?.replace("_", " ")}
          </Badge>
        </div>
      </div>

      <div className="grid lg:grid-cols-2 gap-4 sm:gap-5">
        {/* Details */}
        <div className="bg-white rounded-2xl p-5 sm:p-6 space-y-5" style={{boxShadow: '0 1px 3px rgba(0,0,0,0.04)', border: '1px solid rgba(0,0,0,0.04)'}}>
          <h2 className="text-sm font-semibold text-slate-700 font-['Outfit']">Job Details</h2>
          <div className="space-y-4">
            {job.scheduled_date && (
              <div className="flex items-start gap-3"><Calendar className="w-4 h-4 text-slate-300 mt-0.5" /><div><p className="text-xs text-slate-300">Scheduled Date</p><p className="text-sm font-medium text-slate-700">{formatDateFull(job.scheduled_date)}</p></div></div>
            )}
            {(formatJobScheduleDisplay(job)) && (
              <div className="flex items-start gap-3"><Clock className="w-4 h-4 text-slate-300 mt-0.5" /><div><p className="text-xs text-slate-300">Time</p><p className="text-sm font-medium text-slate-700">{formatJobScheduleDisplay(job)}</p></div></div>
            )}
            {job.address && (
              <div className="flex items-start gap-3"><MapPin className="w-4 h-4 text-slate-300 mt-0.5" /><div><p className="text-xs text-slate-300">Location</p><p className="text-sm font-medium text-slate-700">{job.address}</p></div></div>
            )}
            {job.technician_name && (
              <div className="flex items-start gap-3"><User className="w-4 h-4 text-slate-300 mt-0.5" /><div><p className="text-xs text-slate-300">Technician</p><p className="text-sm font-medium text-slate-700">{job.technician_name}</p></div></div>
            )}
            {job.description && (
              <div className="pt-4 border-t border-slate-50"><p className="text-xs text-slate-300 mb-1.5">Description</p><p className="text-sm text-slate-600">{job.description}</p></div>
            )}
            {job.notes && (
              <div className="pt-4 border-t border-slate-50"><p className="text-xs text-slate-300 mb-1.5">Notes</p><p className="text-sm text-slate-600">{job.notes}</p></div>
            )}
          </div>
        </div>

        {/* Related Invoices */}
        <div className="bg-white rounded-2xl p-5 sm:p-6" style={{boxShadow: '0 1px 3px rgba(0,0,0,0.04)', border: '1px solid rgba(0,0,0,0.04)'}}>
          <h2 className="text-sm font-semibold text-slate-700 font-['Outfit'] mb-4">Related Invoices</h2>
          {!job.invoices || job.invoices.length === 0 ? (
            <div className="text-center py-8"><FileText className="w-8 h-8 mx-auto text-slate-200 mb-2" /><p className="text-slate-300 text-sm">No invoices for this job</p></div>
          ) : (
            <div className="space-y-2">
              {job.invoices.map((invoice) => (
                <Link key={invoice.invoice_id} to={`/portal/invoices/${invoice.invoice_id}`} className="flex items-center justify-between p-3 rounded-xl hover:bg-slate-50 transition-colors" data-testid={`job-invoice-${invoice.invoice_id}`}>
                  <div className="flex items-center gap-3">
                    <div className="w-1 h-8 rounded-full" style={{background: getStatusColor(invoice.status)}} />
                    <div>
                      <p className="text-sm font-medium text-slate-700">#{invoice.invoice_number || invoice.invoice_id?.slice(0, 8)}</p>
                      <p className="text-xs text-slate-300">{formatDate(invoice.created_at)}</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-sm font-semibold text-slate-700">${(invoice.total || 0).toFixed(2)}</p>
                    <Badge className="text-[10px] px-1.5 py-0 border" style={{background: `${getStatusColor(invoice.status)}08`, color: getStatusColor(invoice.status), borderColor: `${getStatusColor(invoice.status)}20`}}>{invoice.status}</Badge>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export { PortalJobs, PortalJobDetails };
